import type { JSX, ReactNode } from 'react';
import './Spread.css';

interface SpreadProps {
  /** Verso (left) page contents. Layout inside is the phase's responsibility. */
  verso: ReactNode;
  /** Recto (right) page contents. Layout inside is the phase's responsibility.
   *  Optional only because `closed` mode renders the verso page alone. */
  recto?: ReactNode;
  /**
   * Optional cinematic overlay positioned absolute over the spread, above
   * the spine and pages. Used by the Lobby → GhostMeeting page-turn (a
   * rotating leaf that occupies the recto half). Pointer events are
   * disabled by default so the overlay doesn't intercept clicks meant for
   * the spread beneath; opt back in inside the overlay's own CSS if
   * interactivity is needed.
   */
  overlay?: ReactNode;
  /**
   * POV-determining chromatic palette.
   *
   * - `'village'` (default) — warm foxed-paper foundation. Lobby and the
   *   Villager / Fool meeting POV both render in this register.
   * - `'ghost'` — hand-authored Silver-Blue palette hue-locked at 225–235°,
   *   plus cool corner vignettes and a deeper cool drop-shadow. The Ghost
   *   meeting POV uses this; the lobby never does (the village hasn't
   *   been haunted yet).
   *
   * The two POVs render the SAME DOM shape — same paper, same twin rules,
   * same corner-wear, same spine, same drop-shadow silhouette. They only
   * differ in the `--page-*` token set the chrome reads from. This is
   * load-bearing for the Lobby → GhostMeeting page-turn cinematic: it can
   * paint the destination palette on the back face of a turning page leaf
   * and the rest of the book stays still.
   */
  pov?: 'village' | 'ghost';
  /** Optional aria-label for the spread group; renders only when provided. */
  ariaLabel?: string;
  /**
   * Closed-book mode: renders a SINGLE page (the verso content) at the book's
   * true per-page proportion (410:660), keeping all the same chrome — paper
   * scan, twin rules, corner-wear, drop-shadow, --page-* tokens — plus a
   * page-stack fore-edge so it reads as the same chapbook, shut. Used by the
   * GameEnd village win ("the book closes"). `recto` is ignored when closed.
   */
  closed?: boolean;
}

/**
 * The chapbook two-page spread — the shared primitive used by every phase
 * that lays content out on a book. Owns all chrome (paper scans, per-page
 * twin rules, foliate spine, corner-wear `border-radius`, drop-shadow, the
 * three-cap width formula, mobile collapse, the short-desktop margin rule).
 * Per-phase content goes in the verso/recto slots; phases never reimplement
 * the chrome.
 *
 * Phase wrappers (`.susdle-lobby`, `.susdle-meeting`) are responsible for
 * vertically centering the spread inside the PageFrame's inner
 * area and adding any phase-specific banner row above it.
 */
export function Spread({
  verso,
  recto,
  pov = 'village',
  ariaLabel,
  overlay,
  closed = false,
}: SpreadProps): JSX.Element {
  const group = ariaLabel ? { role: 'group' as const, 'aria-label': ariaLabel } : {};

  // Closed book: the same chrome, a single verso page at the per-page proportion.
  if (closed) {
    return (
      <div className="susdle-spread susdle-spread--closed" data-pov={pov} data-closed="yes" {...group}>
        <section className="susdle-spread__page susdle-spread__page--verso">{verso}</section>
        {overlay}
      </div>
    );
  }

  return (
    <div className="susdle-spread" data-pov={pov} {...group}>
      <section className="susdle-spread__page susdle-spread__page--verso">
        {verso}
      </section>
      <div aria-hidden className="susdle-spread__spine" />
      <section className="susdle-spread__page susdle-spread__page--recto">
        {recto}
      </section>
      {overlay}
    </div>
  );
}
